import { ControlledTextField } from 'components/Form';
import { ProjectMethodology } from 'components/ProjectMethodology';
import type { OtherCostCalculationTypeEnum } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import type { TypeItem } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { TYPE_VARIANTS } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { FieldLabel, Grid, GridItem, RadioGroup, Text, cnConsta } from 'uikit';

import type { ReactNode } from 'react';
import type { Control, UseFormGetValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { CatalogCostModalTab, calculationTypeLabels, getSectionItems } from '../../catalogOtherAndLimitedHelpers';
import type { TCatalogCostFormValues, TCatalogSectionItem } from '../../types';
import { CatalogOtherAndLimitedTab } from '../../types';

import { CatalogCostTypeSelect } from './CatalogCostTypeSelect';

import Styles from './createOrEditCatalogCostModal.module.css';

const COMPONENT_NAME = 'TextField' as const;
const cnTextField = cnConsta(COMPONENT_NAME);

const labeledRowClassName = cnTextField({
    labelPosition: 'left',
    size: 's',
    view: 'default',
    width: 'full',
    lineHeight: 'm',
});

const getItemLabel = (item: { label: string }) => item.label;

const invisibleStyle = {
    visibility: 'hidden' as const,
    position: 'absolute' as const,
    overflow: 'hidden' as const,
    maxHeight: 0,
};

const LabeledRow = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className={`${labeledRowClassName} ${Styles.labeledRow}`}>
        <FieldLabel size="s" className={cnTextField('Label', ['TextField-Label'])}>
            {label}
        </FieldLabel>
        {children}
    </div>
);

LabeledRow.displayName = 'LabeledRow';

type TCatalogCostModalCommonTabProps = {
    activeTabId: CatalogCostModalTab;
    tab: CatalogOtherAndLimitedTab;
    isCreating: boolean;
    isAdditionalTransport?: boolean;
    control: Control<TCatalogCostFormValues>;
    getValues: UseFormGetValues<TCatalogCostFormValues>;
    selectedType: TypeItem;
    selectedSection: TCatalogSectionItem;
    currentItemId?: number;
    currentTypeUniqueId?: string;
    onSelectedTypeChange: ({ value }: { value: TypeItem }) => void;
    onSelectedSectionChange: ({ value }: { value: TCatalogSectionItem }) => void;
};

export const CatalogCostModalCommonTab = ({
    activeTabId,
    tab,
    isCreating,
    isAdditionalTransport,
    control,
    getValues,
    selectedType,
    selectedSection,
    currentItemId,
    currentTypeUniqueId,
    onSelectedTypeChange,
    onSelectedSectionChange,
}: TCatalogCostModalCommonTabProps) => {
    const commonTabStyle = activeTabId === CatalogCostModalTab.Common ? undefined : invisibleStyle;
    const methodology = useWatch({ control, name: 'methodology' })?.id;
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;
    const sectionItems = getSectionItems(tab, isAdditionalTransport);
    const sectionLabel = isOthersTab ? 'Раздел' : 'Глава в расчете';
    const radioGroupClassName = `${Styles.radioGroup} ${Styles.radioGroupSmall}`;

    return (
        <Grid cols="1" rowGap="s" style={commonTabStyle}>
            <GridItem>
                <ControlledTextField
                    name="name"
                    label="Наименование"
                    type="textarea"
                    control={control}
                    required
                    rules={{
                        required: 'Обязательное поле',
                        maxLength: {
                            value: 255,
                            message: 'Не более 255 символов',
                        },
                    }}
                    isTooltipHidden={!!commonTabStyle}
                />
            </GridItem>
            <GridItem>
                <LabeledRow label="Методология">
                    <ProjectMethodology methodology={methodology ?? ProjectMethodologyEnum.Uer} />
                </LabeledRow>
            </GridItem>
            {isOthersTab && (
                <GridItem>
                    <LabeledRow label="Способ расчета">
                        {isCreating ? (
                            <RadioGroup
                                name="calculatingType"
                                direction="row"
                                items={TYPE_VARIANTS}
                                value={selectedType}
                                getItemLabel={getItemLabel}
                                onChange={onSelectedTypeChange}
                                className={radioGroupClassName}
                            />
                        ) : (
                            <Text size="s">
                                {calculationTypeLabels[selectedType.id as OtherCostCalculationTypeEnum]}
                            </Text>
                        )}
                    </LabeledRow>
                </GridItem>
            )}
            <GridItem>
                <LabeledRow label={sectionLabel}>
                    {isCreating ? (
                        <RadioGroup
                            name="section"
                            direction="row"
                            items={sectionItems}
                            value={selectedSection}
                            getItemLabel={getItemLabel}
                            onChange={onSelectedSectionChange}
                            className={radioGroupClassName}
                        />
                    ) : (
                        <Text size="s">{selectedSection.label}</Text>
                    )}
                </LabeledRow>
            </GridItem>
            <GridItem>
                <CatalogCostTypeSelect
                    control={control}
                    tab={tab}
                    methodology={methodology ?? ProjectMethodologyEnum.Uer}
                    currentTypeName={getValues('costType')?.name}
                    currentTypeUniqueId={currentTypeUniqueId ?? getValues('costType')?.id}
                    currentItemId={currentItemId}
                    isTooltipHidden={!!commonTabStyle}
                />
            </GridItem>
        </Grid>
    );
};

CatalogCostModalCommonTab.displayName = 'CatalogCostModalCommonTab';
